import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { X, Check, Copy, CalendarCheck, AlertTriangle, Info, Zap, CreditCard } from 'lucide-react'
import type { Booking, Child, Event, SocialSecurityFund } from '../api/client'
import { deleteBooking, fetchBookings, fetchChildren, fetchSocialSecurityFunds, fetchUpcomingEvents, updateBooking } from '../api/client'
import { BookingInfoSection } from '../components/BookingInfoSection'
import { SocialSecurityFundSelect, SocialSecuritySubFundSelect } from '../components/SocialSecuritySelect'

// Cancel Confirmation Modal Component
function CancelConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isCancelling
}: { 
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isCancelling: boolean;
}) {
  const { t } = useTranslation(['bookings', 'common']);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-xl border bg-card text-card-foreground shadow-lg w-full max-w-md mx-4 p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-foreground">
            {t('modal.cancelBookingTitle', { ns: 'bookings', defaultValue: 'Cancel Booking' })}
          </h2>
          <button 
            onClick={onClose} 
            className="text-foreground hover:text-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <p className="mb-6 text-muted-foreground">
          {t('modal.cancelBookingMessage', { ns: 'bookings', defaultValue: 'Are you sure you want to cancel this booking? This action cannot be undone.' })}
        </p>
        
        <div className="flex justify-end space-x-4">
          <button 
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors shadow h-9 px-4 py-2 bg-gray-200 text-foreground py-2 px-4 flex items-center justify-center"
          >
            {t('buttons.cancel', { ns: 'common', defaultValue: 'Cancel' })}
          </button>
          <button 
            onClick={onConfirm}
            disabled={isCancelling}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 py-2 px-4 flex items-center justify-center"
          >
            {isCancelling ? (
              <>
                <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('buttons.cancelling', { ns: 'bookings', defaultValue: 'Cancelling...' })}
              </>
            ) : (
              t('modal.confirmCancel', { ns: 'bookings', defaultValue: 'Yes, Cancel Booking' })
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EditBookingPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['bookings', 'common', 'payments'])
  
  const [booking, setBooking] = useState<Booking | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [friendError, setFriendError] = useState<string | null>(null)
  const [socialSecurityFundError, setSocialSecurityFundError] = useState<string | null>(null)
  const [children, setChildren] = useState<Child[]>([])
  const [socialSecurityFunds, setSocialSecurityFunds] = useState<SocialSecurityFund[]>([])
  
  // Form fields
  const [friendRegistrationNumbers, setFriendRegistrationNumbers] = useState<string[]>([])
  const [originalFriendRegistrationNumbers, setOriginalFriendRegistrationNumbers] = useState<string[]>([])
  const [friendRegistrationInput, setFriendRegistrationInput] = useState<string>('')
  const [copySuccess, setCopySuccess] = useState<boolean>(false)
  const [selectedSocialSecurityFund, setSelectedSocialSecurityFund] = useState<number | null>(null)
  const [selectedSocialSecuritySubFund, setSelectedSocialSecuritySubFund] = useState<number | null>(null)
  const [dypaProtocolNumber, setDypaProtocolNumber] = useState<string>('')
  const [dypaProtocolError, setDypaProtocolError] = useState<string | null>(null)
  const [efkaVoucherNumber, setEfkaVoucherNumber] = useState<string>('')
  const [efkaVoucherError, setEfkaVoucherError] = useState<string | null>(null)

  // New state for modal and cancellation
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  
  // New state for arrival timeslots
  const [events, setEvents] = useState<Event[]>([])
  const [selectedArrivalTimeslot, setSelectedArrivalTimeslot] = useState<number | null>(null)
  
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return
      
      setLoading(true)
      setError(null)
      
      try {
        // Fetch booking data and other necessary data in parallel
        const [bookingsData, childrenData, fundsData, eventsData] = await Promise.all([
          fetchBookings(),
          fetchChildren(),
          fetchSocialSecurityFunds(),
          fetchUpcomingEvents()
        ])
        
        const currentBooking = bookingsData.find(b => b.id === parseInt(id, 10))
        
        if (!currentBooking) {
          setError(t('errors.bookingNotFound', { ns: 'bookings' }))
          setLoading(false)
          return
        }
        
        // Ensure the booking has friend_registration_numbers initialized
        if (!currentBooking.friend_registration_numbers) {
          currentBooking.friend_registration_numbers = [];
        }
        
        // Initialize social security fields if they don't exist
        if (currentBooking.social_security_fund_id === undefined) {
          // Check if there's a nested social_security_fund object
          if (currentBooking.social_security_fund && typeof currentBooking.social_security_fund === 'object') {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.social_security_fund_id = currentBooking.social_security_fund.id;
          } else {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.social_security_fund_id = null;
          }
        }
        
        if (currentBooking.social_security_sub_fund_id === undefined) {
          // Check if there's a nested social_security_sub_fund object
          if (currentBooking.social_security_sub_fund && typeof currentBooking.social_security_sub_fund === 'object') {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.social_security_sub_fund_id = currentBooking.social_security_sub_fund.id;
          } else {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.social_security_sub_fund_id = null;
          }
        }
        
        if (currentBooking.dypa_protocol_number === undefined) {
          currentBooking.dypa_protocol_number = '';
        }

        if (currentBooking.efka_voucher_number === undefined) {
          currentBooking.efka_voucher_number = '';
        }

        // Initialize arrival timeslot fields
        if (currentBooking.arrival_timeslot_id === undefined) {
          if (currentBooking.arrival_timeslot && typeof currentBooking.arrival_timeslot === 'object') {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.arrival_timeslot_id = currentBooking.arrival_timeslot.id;
          } else {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.arrival_timeslot_id = null;
          }
        }
        
        setBooking(currentBooking)
        setChildren(childrenData || [])
        setSocialSecurityFunds(fundsData || [])
        setEvents(eventsData || [])
        
        // Set social security fund values - now using null check instead of undefined
        if (currentBooking.social_security_fund_id !== null && currentBooking.social_security_fund_id !== undefined) {
          setSelectedSocialSecurityFund(currentBooking.social_security_fund_id);
          
          // Find the fund in the fetched funds data
          const fund = fundsData.find(f => f.id === currentBooking.social_security_fund_id);
          if (fund) {
            // If there's also a sub-fund ID, set it
            if (currentBooking.social_security_sub_fund_id !== null && currentBooking.social_security_sub_fund_id !== undefined) {
              setSelectedSocialSecuritySubFund(currentBooking.social_security_sub_fund_id);
            }
          }
        } else {
          // Explicitly set to null to ensure the state is initialized
          setSelectedSocialSecurityFund(null);
          setSelectedSocialSecuritySubFund(null);
        }
        
        // Set arrival timeslot if it exists
        if (currentBooking.arrival_timeslot_id !== null && currentBooking.arrival_timeslot_id !== undefined) {
          setSelectedArrivalTimeslot(currentBooking.arrival_timeslot_id);
        } else {
          setSelectedArrivalTimeslot(null);
        }
        
        // Set DYPA protocol number if it exists
        if (currentBooking.dypa_protocol_number) {
          setDypaProtocolNumber(currentBooking.dypa_protocol_number);
        } else {
          setDypaProtocolNumber('');
        }

        // Set EFKA voucher number if it exists
        if (currentBooking.efka_voucher_number) {
          setEfkaVoucherNumber(currentBooking.efka_voucher_number);
        } else {
          setEfkaVoucherNumber('');
        }

        // If there are friend registration numbers, set them
        if (currentBooking.friend_registration_numbers && currentBooking.friend_registration_numbers.length > 0) {
          setFriendRegistrationNumbers([...currentBooking.friend_registration_numbers])
          // Store the original friend registration numbers to compare later
          setOriginalFriendRegistrationNumbers([...currentBooking.friend_registration_numbers])
        } else {
          setFriendRegistrationNumbers([]);
          setOriginalFriendRegistrationNumbers([]);
        }
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'bookings' }))
      } finally {
        setLoading(false)
      }
    }
    
    fetchData()
  }, [id, t])
  
  // Reset sub-fund when fund changes, but only after initial load and only when manually changed
  useEffect(() => {
    // Skip this effect during the initial load or if there's no booking yet
    if (!booking) return;
    
    // Get the booking's fund ID, defaulting to null if undefined
    const bookingFundId = booking.social_security_fund_id !== undefined ? booking.social_security_fund_id : null;
    
    // Skip this effect if the selected fund is the same as the booking's fund
    // This prevents resetting during the initial load
    if (selectedSocialSecurityFund === bookingFundId) {
      return;
    }
    
    // Only reset if the fund has actually changed (not during initial load)
    setSelectedSocialSecuritySubFund(null);
  }, [selectedSocialSecurityFund, booking]);
  
  // Get the selected fund object
  const selectedFund = socialSecurityFunds.find(fund => fund.id === selectedSocialSecurityFund)
  
  // Get the sub-funds for the selected fund
  const subFunds = selectedFund?.sub_funds || []
  
  // Check if the selected fund has sub-funds
  const hasSocialSecuritySubFunds = selectedFund && selectedFund.sub_funds.length > 0
  
  // Get the current event and its arrival timeslots
  const currentEvent = booking ? events.find(event => event.id === booking.event) : null
  const arrivalTimeslots = currentEvent?.arrival_timeslots || []
  
  // Check if arrival timeslot selection should be enabled
  const canSelectArrivalTimeslot = booking && (booking.status === 'confirmed' || booking.status === 'completed')
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!booking || !id) return
    
    // Validate social security fund and sub-fund
    if (selectedSocialSecurityFund && hasSocialSecuritySubFunds && !selectedSocialSecuritySubFund) {
      // Don't set the top error message, as we're showing it under the field
      return
    }
    
    // Validate DYPA protocol number if ΔΥΠΑ is selected and value is provided
    if (selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && dypaProtocolNumber) {
      const { isValid, error } = validateDypaProtocolNumber(dypaProtocolNumber);
      if (!isValid) {
        setDypaProtocolError(error);
        return;
      }
    }

    // Validate EFKA voucher number if ΕΦΚΑ is selected and value is provided
    if (selectedSocialSecurityFund && selectedFund?.name === 'ΕΦΚΑ' && efkaVoucherNumber) {
      const { isValid, error } = validateEfkaVoucher(efkaVoucherNumber);
      if (!isValid) {
        setEfkaVoucherError(error);
        return;
      }
    }

    setSubmitting(true)
    setError(null)
    setFriendError(null)
    setSocialSecurityFundError(null)
    
    try {
      // Set dypaProtocolNumber to empty string if the selected fund is not ΔΥΠΑ
      const finalDypaProtocolNumber =
        selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ'
          ? dypaProtocolNumber
          : '';

      // Set efkaVoucherNumber to empty string if the selected fund is not ΕΦΚΑ
      const finalEfkaVoucherNumber =
        selectedSocialSecurityFund && selectedFund?.name === 'ΕΦΚΑ'
          ? efkaVoucherNumber
          : '';

      // Create the base data object - start empty
      const baseData: any = {};
      
      // Only include friend_registration_numbers if status allows editing them
      const canEditFriends = !['completed', 'cancelled'].includes(booking.status);
      
      if (canEditFriends) {
        baseData.friend_registration_numbers = friendRegistrationNumbers;
      }
      
      // Only include social security fund fields if the booking status allows editing them
      const isStatusAllowingFundEdit = !['info-verified', 'confirmed', 'completed', 'cancelled'].includes(booking.status);
      
      // Include arrival timeslot if status is confirmed or completed
      if (booking.status === 'confirmed' || booking.status === 'completed') {
        baseData.arrival_timeslot_id = selectedArrivalTimeslot || undefined;
      }
      
      if (isStatusAllowingFundEdit) {
        // Include social security fund ID only if the status allows editing
        baseData.social_security_fund_id = selectedSocialSecurityFund || undefined;
        
        // Include DYPA protocol number only if applicable and status allows editing
        if (selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ') {
          baseData.dypa_protocol_number = finalDypaProtocolNumber;
        } else {
          baseData.dypa_protocol_number = '';
        }

        // Include EFKA voucher number only if applicable and status allows editing
        if (selectedSocialSecurityFund && selectedFund?.name === 'ΕΦΚΑ') {
          baseData.efka_voucher_number = finalEfkaVoucherNumber;
        } else {
          baseData.efka_voucher_number = '';
        }

        // Include sub-fund only if applicable and status allows editing
        if (selectedSocialSecurityFund && hasSocialSecuritySubFunds && selectedSocialSecuritySubFund) {
          baseData.social_security_sub_fund_id = selectedSocialSecuritySubFund;
        } else {
          baseData.social_security_sub_fund_id = undefined;
        }
      } else if (booking.status !== 'completed' && booking.status !== 'cancelled') {
        // For 'info-verified' and 'confirmed' statuses, only include DYPA protocol number if needed
        // but don't include the fund IDs to avoid the API error
        if (selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && booking.status !== 'confirmed') {
          baseData.dypa_protocol_number = finalDypaProtocolNumber;
        }
        if (selectedSocialSecurityFund && selectedFund?.name === 'ΕΦΚΑ' && booking.status !== 'confirmed') {
          baseData.efka_voucher_number = finalEfkaVoucherNumber;
        }
      }
      
      const result = await updateBooking(parseInt(id, 10), baseData)
      
      // Update the local booking state with the server response
      setBooking(result);
      
      // Update the original friend registration numbers to match the current ones
      // This will hide the "Added Friends" section after saving
      setOriginalFriendRegistrationNumbers([...friendRegistrationNumbers]);
      
      navigate('/') // Go back to home page after successful update
    } catch (err: any) {
      // Check for specific error messages from the API
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Check for friend-related errors
        if (errorData.friend_registration_numbers) {
          const friendError = Array.isArray(errorData.friend_registration_numbers) 
            ? errorData.friend_registration_numbers[0] 
            : errorData.friend_registration_numbers;
          
          // Check for gender validation error
          if (friendError.includes('same gender')) {
            setFriendError(t('errors.friendGenderMismatch', { ns: 'bookings' }))
          } 
          // Check for invalid registration numbers
          else if (friendError.includes('Invalid registration numbers')) {
            setFriendError(t('errors.invalidRegistrationNumbers', { ns: 'bookings' }))
          }
          // Check for age difference error
          else if (friendError.includes('within 1.5 years of age difference')) {
            setFriendError(t('errors.ageDifference', { ns: 'bookings' }))
          }
          else if (friendError.includes('resulting group would include children with an age difference')) {
            setFriendError(t('errors.groupAgeDifference', { ns: 'bookings' }))
          }
          // Check for no booking error
          else if (friendError.includes('must have their own booking for the same period')) {
            setFriendError(t('errors.noBooking', { ns: 'bookings' }))
          }
          // Check for cancelled booking error
          else if (friendError.includes('must have an active booking')) {
            setFriendError(t('errors.cancelledBooking', { ns: 'bookings' }))
          }
          // Other friend-related errors
          else {
            setFriendError(friendError)
          }
        } 
        // Check for social security fund errors
        else if (errorData.social_security_fund) {
          const fundError = Array.isArray(errorData.social_security_fund) 
            ? errorData.social_security_fund[0] 
            : errorData.social_security_fund;
          
          // Check if it's the one booking per year error
          if (fundError.includes('one booking per year') || fundError.includes('non-private social security fund')) {
            setSocialSecurityFundError(t('errors.oneNonPrivateBookingPerYear', { ns: 'bookings' }));
          } else {
            setSocialSecurityFundError(fundError);
          }
        }
        else {
          setError(t('errors.updateFailed', { ns: 'bookings' }))
        }
      } else {
        setError(t('errors.updateFailed', { ns: 'bookings' }))
      }
    } finally {
      setSubmitting(false)
    }
  }
  
  // Validate registration number format: TR-{year}-{5 digits}
  const isValidRegistrationNumber = (regNumber: string): boolean => {
    const regNumberPattern = /^TR-\d{4}-\d{5}$/;
    return regNumberPattern.test(regNumber);
  }

  const addFriendRegistration = () => {
    if (!friendRegistrationInput.trim()) return
    
    // Check if already in the list
    if (friendRegistrationNumbers.includes(friendRegistrationInput.trim())) {
      setFriendError(t('errors.duplicateFriend', { ns: 'bookings' }))
      return
    }
    
    // Validate registration number format
    const regNumber = friendRegistrationInput.trim()
    if (!isValidRegistrationNumber(regNumber)) {
      setFriendError(t('errors.invalidRegistrationFormat', { ns: 'bookings' }))
      return
    }
    
    // Check if the registration number belongs to the booking's child
    if (booking && children.length > 0) {
      const bookingChild = children.find(child => child.id === booking.child)
      if (bookingChild && bookingChild.registration_number === regNumber) {
        setFriendError(t('errors.cannotAddSelf', { ns: 'bookings', defaultValue: "You cannot add the child as their own friend" }))
        return
      }
    }
    
    // Clear any friend errors when adding a new friend
    setFriendError(null)
    
    // First update the local state with all existing friends plus the new one
    const updatedFriendRegistrationNumbers = [...friendRegistrationNumbers, regNumber];
    setFriendRegistrationNumbers(updatedFriendRegistrationNumbers)
    
    // Then update the booking state if it exists
    if (booking) {
      // Create a deep copy of the booking
      const updatedBooking = JSON.parse(JSON.stringify(booking));
      
      // Initialize friend_registration_numbers if it doesn't exist
      if (!updatedBooking.friend_registration_numbers) {
        updatedBooking.friend_registration_numbers = [];
      }
      
      // Set the friend_registration_numbers to match the local state
      updatedBooking.friend_registration_numbers = [...updatedFriendRegistrationNumbers];
      
      // Update the booking state
      setBooking(updatedBooking);
    }
    
    setFriendRegistrationInput('')
  }
  
  const removeFriendRegistration = (index: number) => {
    // Clear any friend errors when removing a friend
    setFriendError(null)
    
    // Get the registration number that's being removed
    const regNumberToRemove = friendRegistrationNumbers[index];
    
    // First update the local state
    const updatedFriendRegistrationNumbers = [...friendRegistrationNumbers];
    updatedFriendRegistrationNumbers.splice(index, 1);
    setFriendRegistrationNumbers(updatedFriendRegistrationNumbers);
    
    // If the removed registration number was in the original list,
    // we should also remove it from originalFriendRegistrationNumbers
    // to maintain the correct relationship between the two arrays
    if (originalFriendRegistrationNumbers.includes(regNumberToRemove)) {
      const updatedOriginalNumbers = [...originalFriendRegistrationNumbers];
      const originalIndex = updatedOriginalNumbers.indexOf(regNumberToRemove);
      if (originalIndex !== -1) {
        updatedOriginalNumbers.splice(originalIndex, 1);
        setOriginalFriendRegistrationNumbers(updatedOriginalNumbers);
      }
    }
    
    // Then update the booking state if it exists
    if (booking) {
      // Create a deep copy of the booking
      const updatedBooking = JSON.parse(JSON.stringify(booking));
      
      // Initialize friend_registration_numbers if it doesn't exist
      if (!updatedBooking.friend_registration_numbers) {
        updatedBooking.friend_registration_numbers = [];
      } else {
        // Set the friend_registration_numbers to match the local state
        updatedBooking.friend_registration_numbers = [...updatedFriendRegistrationNumbers];
      }
      
      // Update the booking state
      setBooking(updatedBooking);
    }
  }
  
  // Get child name from booking
  const getChildName = (childId?: number) => {
    if (!childId) return t('bookingsBox.unknownChild')
    const child = children.find(child => child.id === childId)
    return child ? `${child.first_name} ${child.last_name}` : t('bookingsBox.unknownChild')
  }
  
  // Get child gender
  const getChildGender = (childId?: number) => {
    if (!childId) return null
    const child = children.find(child => child.id === childId)
    return child ? child.gender : null
  }
  
  // Get gender-based styling for child
  const getGenderStyling = (gender: string | null) => {
    if (gender === 'female') {
      return 'bg-pink-100 text-pink-800 border-pink-200';
    } else if (gender === 'male') {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else {
      return 'bg-gray-100 text-foreground border-gray-200';
    }
  }
  
  // Get status badge color based on status
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'finalized':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'interested':
      case 'awaiting-communication':
      case 'info-verified':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'confirmed':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'detail_checking':
        return 'bg-sky-100 text-sky-800 border-sky-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'completed':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      default:
        return 'bg-gray-100 text-foreground border-gray-200';
    }
  }
  
  // Copy booking code to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  // Remove a masked friend
  const removeMaskedFriend = (index: number) => {
    if (!booking || !booking.masked_friends) return;
    
    // Create a deep copy of the booking
    const updatedBooking = JSON.parse(JSON.stringify(booking));
    
    // Create a copy of masked_friends array and remove the friend at the specified index
    if (!updatedBooking.masked_friends) {
      updatedBooking.masked_friends = [];
    } else {
      updatedBooking.masked_friends.splice(index, 1);
    }
    
    // If the booking has friend_registration_numbers, update those too
    if (updatedBooking.friend_registration_numbers) {
      // Only remove if the index is valid
      if (index < updatedBooking.friend_registration_numbers.length) {
        // Get the registration number that's being removed
        const regNumberToRemove = updatedBooking.friend_registration_numbers[index];
        
        // Create a copy of the friend_registration_numbers array
        const updatedRegNumbers = [...updatedBooking.friend_registration_numbers];
        updatedRegNumbers.splice(index, 1);
        updatedBooking.friend_registration_numbers = updatedRegNumbers;
        
        // Update the local state for friend registration numbers
        setFriendRegistrationNumbers([...updatedRegNumbers]);
        
        // If the removed registration number was in the original list,
        // we should also remove it from originalFriendRegistrationNumbers
        if (originalFriendRegistrationNumbers.includes(regNumberToRemove)) {
          const updatedOriginalNumbers = [...originalFriendRegistrationNumbers];
          const originalIndex = updatedOriginalNumbers.indexOf(regNumberToRemove);
          if (originalIndex !== -1) {
            updatedOriginalNumbers.splice(originalIndex, 1);
            setOriginalFriendRegistrationNumbers(updatedOriginalNumbers);
          }
        }
      }
    }
    
    // Update the booking state
    setBooking(updatedBooking);
    
    // Clear any friend errors
    setFriendError(null);
  };
  
  // Check if there are newly added friends that haven't been saved yet
  const hasNewlyAddedFriends = () => {
    // Get only the friends that are in friendRegistrationNumbers but not in originalFriendRegistrationNumbers
    const newlyAddedFriends = friendRegistrationNumbers.filter(
      regNum => !originalFriendRegistrationNumbers.includes(regNum)
    );
    
    // Return true if there are any newly added friends
    return newlyAddedFriends.length > 0;
  };
  
  // New function to handle booking cancellation
  const handleCancelBooking = async () => {
    if (!id) return;
    
    setIsCancelling(true);
    setError(null);
    
    try {
      await deleteBooking(parseInt(id, 10));
      // Navigate back to home page after successful cancellation
      navigate('/');
    } catch (err: any) {
      console.error('Error cancelling booking:', err);
      setError(t('errors.cancelFailed', { ns: 'bookings', defaultValue: 'Failed to cancel booking' }));
      setShowCancelModal(false);
    } finally {
      setIsCancelling(false);
    }
  };
  
  // Validate DYPA protocol number format: XX/YYYY/XXXXXXXXXXXX where YYYY must be the current year
  const validateDypaProtocolNumber = (value: string): { isValid: boolean; error: string | null } => {
    if (!value) return { isValid: true, error: null }; // Empty is allowed
    
    const pattern = /^(\d{2})\/(\d{4})\/(\d{12})$/;
    const match = value.match(pattern);
    
    if (!match) {
      return {
        isValid: false,
        error: t('errors.invalidDypaFormat', { 
          ns: 'bookings', 
          defaultValue: 'DYPA protocol number must be in format: XX/YYYY/XXXXXXXXXXXX' 
        })
      };
    }
    
    // Extract the year from the protocol number
    const year = parseInt(match[2], 10);
    const currentYear = new Date().getFullYear();
    
    if (year !== currentYear) {
      return {
        isValid: false,
        error: t('errors.invalidDypaYear', { 
          ns: 'bookings', 
          defaultValue: `DYPA protocol year must be ${currentYear}`,
          year: currentYear
        })
      };
    }
    
    return { isValid: true, error: null };
  };
  
  const handleDypaProtocolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDypaProtocolNumber(value);
    // Clear error when user types
    setDypaProtocolError(null);
  };

  // Format an EFKA voucher to XXXX-XXXX-XXXX-XXXX (16 digits, dash every 4)
  const formatEfkaVoucher = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1-').replace(/-$/, '');
  };

  // Validate EFKA voucher: exactly 16 digits (empty is allowed)
  const validateEfkaVoucher = (value: string): { isValid: boolean; error: string | null } => {
    if (!value) return { isValid: true, error: null };

    if (value.replace(/\D/g, '').length !== 16) {
      return {
        isValid: false,
        error: t('errors.invalidEfkaFormat', {
          ns: 'bookings',
          defaultValue: 'EFKA voucher number must be in format: XXXX-XXXX-XXXX-XXXX'
        })
      };
    }

    return { isValid: true, error: null };
  };

  // Auto-insert dashes as the user types, preserving the cursor position
  const handleEfkaVoucherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const previousValue = input.value;
    const digitsBeforeCursor = previousValue
      .slice(0, input.selectionStart ?? previousValue.length)
      .replace(/\D/g, '').length;

    const formatted = formatEfkaVoucher(previousValue);
    setEfkaVoucherNumber(formatted);
    setEfkaVoucherError(null);

    // Restore the cursor after the same number of digits in the formatted value
    requestAnimationFrame(() => {
      let pos = 0;
      let seen = 0;
      while (pos < formatted.length && seen < digitsBeforeCursor) {
        if (/\d/.test(formatted[pos])) seen++;
        pos++;
      }
      input.setSelectionRange(pos, pos);
    });
  };
  
  if (loading) {
    return (
      <div>
        <div className="max-w-2xl mx-auto">
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        </div>
      </div>
    )
  }
  
  if (!booking) {
    return (
      <div>
        <div className="max-w-2xl mx-auto">
          <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6 p-8">
            <div className="text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-foreground mb-4" />
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {t('errors.bookingNotFound', { ns: 'bookings' })}
              </h2>
              <div className="mt-6">
                <Link to="/" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 disabled:pointer-events-none disabled:opacity-50">
                  {t('common:backToHome')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div>
      <main className="max-w-3xl mx-auto">
        <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center mb-4">
            <CalendarCheck className="h-6 w-6 mr-2 text-foreground" />
            <h1 className="text-2xl font-semibold text-foreground">
              {t('bookingsBox.editBooking')}
            </h1>
          </div>
          
          <div className="mb-6 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1 sm:gap-0">
              <span className="font-light mb-1 sm:mb-0">{t('bookingsBox.code')}:</span>
              <div>
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-md bg-slate-100 text-slate-800 border border-slate-200 inline-flex items-center font-mono">
                  {booking.code}
                  <button 
                    onClick={() => copyToClipboard(booking.code)}
                    className="ml-2 text-primary hover:text-purple-900 focus:outline-none"
                    aria-label="Copy booking code"
                  >
                    {copySuccess ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </span>
                {copySuccess && (
                  <span className="ml-2 text-xs text-emerald-600 animate-fade-in-out">
                    {t('common.copied', { defaultValue: 'Copied!' })}
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1 sm:gap-0">
              <span className="font-light mb-1 sm:mb-0">{t('bookingsBox.child')}:</span>
              <div>
                {booking.child ? (
                  <Link 
                    to={`/children/${booking.child}`} 
                    className={`px-2 py-1 text-sm font-medium border-2 rounded-full ${getGenderStyling(getChildGender(booking.child))}`}
                  >
                    {getChildName(booking.child)}
                  </Link>
                ) : (
                  <span className="font-light">{t('bookingsBox.unknownChild')}</span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1 sm:gap-0">
              <span className="font-light mb-1 sm:mb-0">{t('bookingsBox.event')}:</span>
              <div>
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-md bg-amber-100 text-amber-800 border border-amber-200">
                  {booking.event_name || t('bookingsBox.unknownEvent')}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1 sm:gap-0">
              <span className="font-light mb-1 sm:mb-0">{t('bookingsBox.status')}:</span>
              <div>
                <span className={`px-2 py-1 text-sm font-medium rounded-full ${getStatusColor(booking.status)}`}>
                  {t(`bookingsBox.statuses.${booking.status}`)}
                </span>
              </div>
            </div>

            
            {booking.in_waiting_list && (
            <div className="grid grid-cols-1 sm:grid-cols items-start sm:items-center gap-1 sm:gap-0">
              <span className="text-destructive text-xs font-bold flex items-center">
                <Zap className="h-4 w-4 mr-1 text-red-500" />
                {t('bookingsBox.waitingList')}
              </span>
            </div>
            )}

            {booking.payments_info && booking.payments_info.length > 0 && (
              <>
                <hr className="border-border" />
                <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start gap-1 sm:gap-0">
                  <span className="font-light mb-1 sm:mb-0 flex items-center gap-1">
                    <CreditCard className="h-4 w-4" />
                    {t('bookingsBox.relatedPayments')}:
                  </span>
                  <div className="space-y-1.5">
                    {booking.payments_info.map((payment) => (
                      <div key={payment.id} className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 text-slate-800 border border-slate-200 rounded-md">
                          {payment.merchant_reference}
                        </span>
                        <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-blue-100 text-blue-800 border border-blue-200">
                          {t(`types.${payment.payment_type}`, { ns: 'payments' })}
                        </span>
                        <span className="font-medium">
                          {new Intl.NumberFormat(i18n.language === 'el' ? 'el-GR' : 'en-US', { style: 'currency', currency: 'EUR' }).format(parseFloat(payment.amount))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 mb-4">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <div className="flex items-start gap-2 p-2 bg-muted rounded-md text-xs text-muted-foreground mb-4">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>{t('bookingNote')}</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Social Security Fund Selection */}
            <div>
              <label htmlFor="social-security-fund" className="block text-sm font-medium mb-1.5">
                {t('bookingsBox.socialSecurityRegistry')}
              </label>
              <SocialSecurityFundSelect
                funds={socialSecurityFunds}
                value={selectedSocialSecurityFund}
                onChange={(value) => {
                  setSelectedSocialSecurityFund(value);
                  setSocialSecurityFundError(null);
                }}
                placeholder={t('bookingsBox.selectSocialSecurityRegistry')}
                className={`w-full ${socialSecurityFundError ? 'border-2 border-destructive' : ''}`}
                isDisabled={booking.status === 'info-verified' || booking.status === 'confirmed' || booking.status === 'completed' || booking.status === 'cancelled'}
              />
              {socialSecurityFundError && (
                <p className="text-xs text-destructive mt-1">
                  {socialSecurityFundError}
                </p>
              )}
            </div>
            
            {/* Social Security Sub Fund Selection - Only show if the selected fund has sub-funds */}
            {selectedSocialSecurityFund && hasSocialSecuritySubFunds && (
              <div>
                <label htmlFor="social-security-sub-fund" className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.socialSecurityFund')} {hasSocialSecuritySubFunds && '*'}
                </label>
                <SocialSecuritySubFundSelect
                  subFunds={subFunds}
                  value={selectedSocialSecuritySubFund}
                  onChange={setSelectedSocialSecuritySubFund}
                  placeholder={t('bookingsBox.selectSocialSecurityFund')}
                  className="w-full"
                  isDisabled={!selectedSocialSecurityFund || !hasSocialSecuritySubFunds || booking.status === 'info-verified' || booking.status === 'confirmed' || booking.status === 'completed' || booking.status === 'cancelled'}
                />
                {selectedSocialSecurityFund && hasSocialSecuritySubFunds && !selectedSocialSecuritySubFund && (
                  <p className="text-xs text-destructive mt-1">
                    {t('errors.socialSecurityFundRequired', { ns: 'bookings', defaultValue: 'Πρέπει να επιλέξετε Ταμείο Φορέα για αυτόν τον Φορέα Ασφάλισης' })}
                  </p>
                )}
              </div>
            )}
            
            {/* DYPA Protocol Number */}
            {selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && (
              <div>
                <label htmlFor="dypa-protocol-number" className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.dypaProtocolNumber')}
                </label>
                <input
                  type="text"
                  id="dypa-protocol-number"
                  value={dypaProtocolNumber}
                  onChange={handleDypaProtocolChange}
                  className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${dypaProtocolError ? 'border-destructive' : 'border-input'}`}
                  placeholder={`XX/${new Date().getFullYear()}/XXXXXXXXXXXX`}
                  disabled={booking.status === 'confirmed' || booking.status === 'completed' || booking.status === 'cancelled'}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('bookingsBox.dypaProtocolFormat', { ns: 'bookings' })}: XX/{new Date().getFullYear()}/XXXXXXXXXXXX
                </p>
                {dypaProtocolError && (
                  <p className="text-xs text-destructive mt-1">{dypaProtocolError}</p>
                )}
              </div>
            )}

            {/* EFKA Voucher Number */}
            {selectedSocialSecurityFund && selectedFund?.name === 'ΕΦΚΑ' && (
              <div>
                <label htmlFor="efka-voucher-number" className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.efkaVoucherNumber')}
                </label>
                <input
                  type="text"
                  id="efka-voucher-number"
                  value={efkaVoucherNumber}
                  onChange={handleEfkaVoucherChange}
                  className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${efkaVoucherError ? 'border-destructive' : 'border-input'}`}
                  placeholder="XXXX-XXXX-XXXX-XXXX"
                  inputMode="numeric"
                  disabled={booking.status === 'confirmed' || booking.status === 'completed' || booking.status === 'cancelled'}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('bookingsBox.efkaVoucherFormat', { ns: 'bookings' })}: XXXX-XXXX-XXXX-XXXX
                </p>
                {efkaVoucherError && (
                  <p className="text-xs text-destructive mt-1">{efkaVoucherError}</p>
                )}
              </div>
            )}

            {/* Arrival Time Slot Selection - Only show if status is confirmed or completed */}
            {canSelectArrivalTimeslot && arrivalTimeslots.length > 0 && (
              <div>
                <label htmlFor="arrival-timeslot" className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.arrivalAppointment', { ns: 'bookings', defaultValue: 'Arrival Appointment' })}
                </label>
                <select
                  id="arrival-timeslot"
                  value={selectedArrivalTimeslot || ''}
                  onChange={(e) => setSelectedArrivalTimeslot(e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
                  disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                >
                  <option value="">
                    {t('bookingsBox.selectArrivalTime', { ns: 'bookings', defaultValue: 'Select a time slot' })}
                  </option>
                  {arrivalTimeslots
                    .filter(timeslot => !timeslot.is_full)  // Only show timeslots that are not full
                    .map((timeslot) => {
                      const startTime = new Date(timeslot.start_time);
                      const endTime = new Date(timeslot.end_time);
                      // Display full datetime with date and time
                      const dateLabel = startTime.toLocaleDateString('el-GR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      });
                      const timeLabel = `${startTime.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}`;
                      
                      return (
                        <option key={timeslot.id} value={timeslot.id}>
                          {dateLabel} {timeLabel}
                        </option>
                      );
                    })}
                </select>
              </div>
            )}
            
            {/* Friends - Only show when status allows editing */}
            {(booking.status as string) !== 'completed' && (booking.status as string) !== 'cancelled' && (
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {t('bookingsBox.friends')}
              </label>
              
              <div className="mt-2 flex">
                <input
                  type="text"
                  value={friendRegistrationInput}
                  onChange={(e) => {
                    // Clear friend error when user types
                    if (friendError) setFriendError(null);
                    setFriendRegistrationInput(e.target.value);
                  }}
                  placeholder={t('bookingsBox.friendRegistrationPlaceholder')}
                  className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${friendError ? 'border-destructive' : 'border-input'}`}
                  disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                />
                <button
                  type="button"
                  onClick={addFriendRegistration}
                  className="ml-2 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 disabled:pointer-events-none disabled:opacity-50"
                  disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                >
                  {t('bookingsBox.add')}
                </button>
              </div>
              
              <p className="text-xs text-muted-foreground mt-1">
                {t('bookingsBox.registrationNumberFormat')}
              </p>
              
              {friendError && (
                <p className="text-xs text-destructive mt-1">{friendError}</p>
              )}
              
              {/* Only show the Added Friends section if there are newly added friends */}
              {hasNewlyAddedFriends() && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    {t('bookingsBox.addedFriends')}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {friendRegistrationNumbers
                      .filter(regNum => !originalFriendRegistrationNumbers.includes(regNum))
                      .map((regNum, index) => (
                        <div 
                          key={index} 
                          className={`flex items-center rounded-full px-3 py-1 border ${getGenderStyling(getChildGender(booking.child))}`}
                        >
                          <span className="text-sm font-medium mr-2">{regNum}</span>
                          <button
                            type="button"
                            onClick={() => {
                              // Find the index in the full friendRegistrationNumbers array
                              const fullIndex = friendRegistrationNumbers.indexOf(regNum);
                              if (fullIndex !== -1) {
                                removeFriendRegistration(fullIndex);
                              }
                            }}
                            className="text-muted-foreground hover:text-destructive text-xs focus:outline-none"
                            aria-label={t('bookingsBox.remove')}
                            disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              
              {booking.masked_friends && booking.masked_friends.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    {t('bookingsBox.maskedFriends')}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {booking.masked_friends.map((name, index) => {
                      // Since we don't have direct access to the friend's gender,
                      // we can infer it from the booking's child gender since friends must be the same gender
                      const childGender = getChildGender(booking.child);
                      return (
                        <div 
                          key={index} 
                          className={`flex items-center rounded-full px-3 py-1 border ${getGenderStyling(childGender)}`}
                        >
                          <span className="text-sm font-medium mr-2">{name}</span>
                          <button
                            type="button"
                            onClick={() => removeMaskedFriend(index)}
                            className="text-muted-foreground hover:text-destructive text-xs focus:outline-none"
                            aria-label={t('bookingsBox.remove')}
                            disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            )}
            
            {/* Show existing friends if status doesn't allow editing */}
            {((booking.status as string) === 'completed') && booking.masked_friends && booking.masked_friends.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {t('bookingsBox.maskedFriends')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {booking.masked_friends.map((name, index) => {
                    const childGender = getChildGender(booking.child);
                    return (
                      <div 
                        key={index} 
                        className={`flex items-center rounded-full px-3 py-1 border ${getGenderStyling(childGender)}`}
                      >
                        <span className="text-sm font-medium">{name}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('bookingsBox.friendsCannotBeChangedAfterCompletion', { ns: 'bookings', defaultValue: 'Friends cannot be changed after booking is completed' })}
                </p>
              </div>
            )}
            
            {/* Fund-specific Information Section */}
            {selectedSocialSecurityFund && (
              <BookingInfoSection fundName={selectedFund?.name} />
            )}
            
            <div className="flex flex-row gap-4 pt-4">
              <Link to="/bookings" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 w-1/3">
                {t('modal.back', { ns: 'bookings', defaultValue: 'Back' })}
              </Link>
              
              {(booking.status !== 'completed' && booking.status !== 'cancelled') && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowCancelModal(true)}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 w-1/3 flex items-center justify-center"
                  >
                    {t('modal.cancelBooking', { ns: 'bookings', defaultValue: 'Cancel Booking' })}
                  </button>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 disabled:pointer-events-none disabled:opacity-50 w-1/3 flex items-center justify-center"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('buttons.saving')}
                      </>
                    ) : (
                      t('bookingsBox.saveChanges')
                    )}
                  </button>
                </>
              )}
              
              {(booking.status === 'completed' || booking.status === 'cancelled') && (
                <div className="w-2/3"></div> // Spacer div to keep the back button aligned left
              )}
            </div>
          </form>
        </div>
      </main>
      
      {/* Cancel Confirmation Modal */}
      <CancelConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelBooking}
        isCancelling={isCancelling}
      />
    </div>
  )
} 